import DOMPurify from 'dompurify';

/**
 * Sanitize HTML to prevent XSS attacks
 * - Removes all <script> tags
 * - Removes inline event handlers (onclick, onerror, etc.)
 * - Allows all safe HTML tags and attributes
 */
export const sanitizeHtml = (html) => {
  const config = {
    ALLOWED_TAGS: [
      'div', 'span', 'p', 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'img', 'video', 'audio', 'iframe',
      'table', 'thead', 'tbody', 'tr', 'td', 'th',
      'blockquote', 'code', 'pre', 'strong', 'em', 'br', 'hr',
      'section', 'article', 'header', 'footer', 'nav', 'aside',
      'button', 'form', 'input', 'label', 'select', 'option', 'textarea'
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'class', 'id', 'style',
      'width', 'height', 'target', 'rel', 'data-*',
      'type', 'name', 'value', 'placeholder', 'disabled', 'readonly'
    ],
    FORBID_TAGS: ['script', 'noscript', 'object', 'embed', 'link'],
    FORBID_ATTR: ['onerror', 'onclick', 'onload', 'onmouseover', 'onfocus', 'onblur'],
    ALLOW_DATA_ATTR: true,
  };

  return DOMPurify.sanitize(html, config);
};

/**
 * Validate CSS for potentially dangerous content
 * Returns warnings array if issues found
 */
export const validateCss = (css) => {
  const warnings = [];

  // Check for JavaScript in CSS (expression, behavior, etc.)
  // eslint-disable-next-line no-script-url
  if (css.includes('javascript:') || css.includes('expression(')) {
    warnings.push('⚠️ JavaScript detected in CSS - this will be removed');
  }

  // Check for @import of external files (XSS vector)
  if (css.includes('@import')) {
    warnings.push('ℹ️ @import detected - use CDN links field instead for external stylesheets');
  }

  return warnings;
};

/**
 * Scope CSS to a specific container
 * Wraps all selectors with a container class
 */
export const scopeCss = (css, scopeClass = 'custom-html-section') => {
  // Simple scoping: prefix all selectors with the scope class
  // This is a basic implementation - for production, consider using a CSS parser

  const lines = css.split('\n');
  const scopedLines = lines.map(line => {
    // Skip comments, @rules, and empty lines
    if (line.trim().startsWith('/*') ||
        line.trim().startsWith('*') ||
        line.trim().startsWith('@') ||
        line.trim() === '') {
      return line;
    }

    // If line contains a selector (ends with {)
    if (line.includes('{')) {
      const parts = line.split('{');
      const selectors = parts[0].split(',').map(s => s.trim());
      const scopedSelectors = selectors.map(selector => {
        // Don't scope :root, body, html, or keyframes
        if (selector.match(/^(:root|body|html|@keyframes)/)) {
          return selector;
        }
        return `.${scopeClass} ${selector}`;
      });
      return scopedSelectors.join(', ') + ' {' + (parts[1] || '');
    }

    return line;
  });

  return scopedLines.join('\n');
};
